<script setup lang="ts">
/**
 * BaseSelect — components.css 의 .field/.select 패턴 원자.
 *  네이티브 <select> + lucide ChevronDown 표시.
 *  label(+필수) + select + hint + error. vee-validate 연동: v-model + error.
 * 색은 토큰 경유 — is-error 시 보더/메시지 danger.
 */
import { computed, useId } from 'vue';
import { ChevronDown } from 'lucide-vue-next';

import {
  FIELD_CONTROL_BASE_CLASS,
  FIELD_CONTROL_ERROR_CLASS,
  FIELD_ERROR_MSG_CLASS,
  FIELD_HINT_MSG_CLASS,
  FIELD_LABEL_CLASS,
} from '#/lib/fieldStyles';
import { cn } from '#/lib/utils';

interface SelectOption {
  label: string;
  value: string;
}

interface Props {
  /** v-model 값 */
  modelValue?: string;
  /** 라벨 텍스트 */
  label?: string;
  /** 옵션 목록 */
  options?: SelectOption[];
  /** 빈 선택지(플레이스홀더) 라벨 — 지정 시 value='' 옵션 추가 */
  placeholder?: string;
  /**
   * 플레이스홀더를 **다시 고를 수 있게** 한다(선택 해제 허용).
   *
   * 기본값 `false` 는 플레이스홀더 option 을 `disabled` 로 둬서, 한 번 값을 고르면 빈 값으로
   * 되돌릴 수 없다 — 필수 항목에는 이게 맞다(빈 값 재선택 = 검증 실패로 되돌아가는 길).
   *
   * 🔴 **선택 입력 항목에는 반드시 `true` 를 준다.** 취소할 수 없는 선택 입력은 실수로 고른
   * 값을 지울 방법이 없어 사실상 강제 수집이 되고, 가입 폼 세그먼트처럼 "미입력이 정상 경로"인
   * 항목에서는 개보법 §22⑤ 취지에도 어긋난다.
   */
  clearable?: boolean;
  /** 필수 표시(*) */
  required?: boolean;
  /** 보조 안내 문구 */
  hint?: string;
  /** 검증 에러 메시지 */
  error?: string;
  /** 비활성 */
  disabled?: boolean;
  /** name 속성 */
  name?: string;
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: '',
  label: '',
  options: () => [],
  placeholder: '',
  clearable: false,
  required: false,
  hint: '',
  error: '',
  disabled: false,
  name: undefined,
});

const emit = defineEmits<{
  'update:modelValue': [value: string];
  change: [value: string];
}>();

const fieldId = useId();
const hasError = computed(() => props.error.length > 0);

/** 공통 컨트롤 골격 + select padding(좌 14 / 우 10·화살표 자리) + appearance 제거 + 에러 보더. */
const selectClass = computed(() =>
  cn(
    FIELD_CONTROL_BASE_CLASS,
    'appearance-none py-3 pl-[14px] pr-10',
    hasError.value && FIELD_CONTROL_ERROR_CLASS,
  ),
);

function onChange(event: Event) {
  const value = (event.target as HTMLSelectElement).value;
  emit('update:modelValue', value);
  emit('change', value);
}
</script>

<template>
  <div class="mb-4" :class="{ 'is-error': hasError }">
    <label v-if="label" :for="fieldId" :class="FIELD_LABEL_CLASS">
      {{ label }}<span v-if="required" class="ml-[3px] text-danger">*</span>
    </label>

    <div class="relative">
      <select
        :id="fieldId"
        :name="name"
        :value="modelValue"
        :disabled="disabled"
        :aria-invalid="hasError || undefined"
        :class="selectClass"
        @change="onChange"
      >
        <!-- clearable 이면 플레이스홀더를 다시 고를 수 있다(선택 해제) — 선택 입력 항목의 요건. -->
        <option v-if="placeholder" value="" :disabled="!clearable">{{ placeholder }}</option>
        <option v-for="opt in options" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
      </select>
      <ChevronDown
        class="pointer-events-none absolute right-3 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-muted"
      />
    </div>

    <p v-if="hasError" :class="FIELD_ERROR_MSG_CLASS">{{ error }}</p>
    <p v-else-if="hint" :class="FIELD_HINT_MSG_CLASS">{{ hint }}</p>
  </div>
</template>
